import { Box, useEventCallback } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import CoursesApi from '../../../../../apis/CoursesApi';
import UnexpectedError from '../../../../../components/UnexpectedError/UnexpectedError';
import { VideoModuleDto } from '../../../../../generated-types/videoModuleDto';
import useApi from '../../../../../lib/useApi';
import BaseModuleProps from '../../../lib/BaseModuleProps';

/**
 * The properties for the video module
 */
interface VideoModuleProps extends BaseModuleProps {
  /**
   * Set that video skipping has been detected
   */
  setVideoSkipDetected: (
    skipDetected: boolean,
    videoTracking: boolean[],
    duration: number
  ) => void;
}

/**
 * A module to show the user a video
 */
const VideoModule = ({
  moduleId,
  setIsLoading,
  setComplete,
  setVideoSkipDetected,
}: VideoModuleProps) => {
  const falseArray = useCallback(
    () => [...new Array(100)].map(() => false),
    []
  );
  const coursesApi = useApi(CoursesApi);
  const [videoHeight, setVideoHeight] = useState('100%');
  const [videoWidth, setVideoWidth] = useState('100%');
  const [videoWatched, setVideoWatched] = useState(false);
  const [videoTracking, setVideoTracking] = useState(falseArray);
  const [showError, setShowError] = useState(false);
  const [duration, setDuration] = useState(0);
  const [furthestWatchedIndex, setFurthestWatchedIndex] = useState(0);
  const [path, setPath] = useState<string>();
  const [vttPath, setVttPath] = useState<string>();
  const skipCounterLimit = 2;
  // const progressIntervalMs = 300;

  /**
   * Handle the progress updates from the video to ensure the user watches it entirely and that we notify
   *  when a skip has been detected
   */
  const progressUpdate = useEventCallback(
    (e: React.SyntheticEvent<HTMLVideoElement>) => {
      // If the video is watched, we can return early.
      if (videoWatched) return;

      // Get the times
      const currentTime = e.currentTarget.currentTime;
      const totalDuration = e.currentTarget.duration;

      // Figure out what position to consider watched
      const played = currentTime / totalDuration;
      const watchedPercentage = played * 100;
      const roundedPosition = Math.floor(watchedPercentage);
      const index =
        roundedPosition == 100 ? roundedPosition - 1 : roundedPosition;

      // Record the furthest watched
      if (watchedPercentage > furthestWatchedIndex)
        setFurthestWatchedIndex(index);

      // Copy and update the video tracking array
      const copiedVideoTracking = [...videoTracking];
      copiedVideoTracking[index] = true;

      // Check to see if we can consider the video as watched. We only care about 98% of it being watched, to account for the little
      //  bit of dead time and the beginning and end of the videos
      const watchedCount = copiedVideoTracking.reduce(
        (count, watched) => count + (watched ? 1 : 0),
        0
      );
      const isWatched = watchedCount >= 98;
      if (isWatched) {
        setVideoWatched(true);
        return;
      }

      // Starting at the furthest watched index and move backwards.
      let skipCounter = 0;
      for (let i = furthestWatchedIndex; i >= 0; i--) {
        // If it's true, we can set the counter back to 0 and continue
        if (copiedVideoTracking[i]) {
          skipCounter = 0;
          continue;
        }

        // Count the skips
        skipCounter++;

        // End early if the skip counter is above our limit
        if (skipCounter >= skipCounterLimit) break;
      }
      setVideoSkipDetected(
        skipCounter >= skipCounterLimit,
        copiedVideoTracking,
        duration
      );
      setVideoTracking(copiedVideoTracking);
    }
  );

  useEffect(() => {
    setIsLoading(true);
    setVideoTracking(falseArray);
    setDuration(0);
    setFurthestWatchedIndex(0);
    setPath(undefined);
    const init = async () => {
      try {
        const module = await coursesApi.GetModule<VideoModuleDto>(moduleId);
        setPath(module.signedVideoUrl);
        setVttPath(module.signedVttUrl);
      } catch (e) {
        setShowError(true);
      } finally {
        // Turn off loading
        setIsLoading(false);
      }
    };

    init();
  }, [coursesApi, falseArray, moduleId, setIsLoading]);

  useEffect(() => {
    // Setup an observer that can dynamically resize the video box as needed
    const videoBox = document.getElementById('video-box') as Element;
    new ResizeObserver(() => {
      if (videoBox.clientWidth / videoBox.clientHeight > 16 / 9) {
        setVideoHeight('100%');
        setVideoWidth('unset');
      } else {
        setVideoHeight('unset');
        setVideoWidth('100%');
      }
    }).observe(videoBox);
  }, []);

  useEffect(() => {
    if (videoWatched) {
      setComplete();
    }
  }, [falseArray, setComplete, videoWatched]);

  /** Reset the state when the module changes */
  useEffect(() => {
    setVideoWatched(false);
    setVideoTracking(falseArray);
  }, [falseArray, moduleId]);

  return (
    <Box height="100%" width="100%">
      {showError ? (
        <UnexpectedError errorContext="the video module" />
      ) : (
        <Box
          id="video-box"
          data-testid="video-module-id"
          sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {path && (
            <video
              crossOrigin="anonymous"
              controls
              controlsList="nodownload"
              onContextMenu={(e) => e.preventDefault()}
              autoPlay
              width={videoWidth}
              height={videoHeight}
              onTimeUpdate={progressUpdate}
              onDurationChange={(e) => {
                setDuration(e.currentTarget.duration);
              }}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
              }}
            >
              <source src={path} type="video/mp4" />
              {vttPath && (
                <track
                  src={vttPath}
                  kind="captions"
                  srcLang="en"
                  label="English"
                />
              )}
              Your browser does not support HTML video.
            </video>
          )}
        </Box>
      )}
    </Box>
  );
};

export default VideoModule;
